import { brandPalette } from "../../../constants/brandPalette";
import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useMemo } from "react";
import { FaBell } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getAdminNotificationsRequest } from "../../../modules/admin-modules/admin-notifications/Actions";

function safeCount(arr) {
  return Array.isArray(arr) ? arr.length : 0;
}

export default function AdminNotificationsBell({
  iconColor = "white",
  hoverBg = "rgba(255,255,255,0.12)",
  variant = "ghost",
  size = "md",
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { video_requests, redo_exams, hw, isLoading } = useSelector(
    (s) => s.adminNotifications || {}
  );

  useEffect(() => {
    dispatch(getAdminNotificationsRequest());
    const t = setInterval(() => dispatch(getAdminNotificationsRequest()), 60_000);
    return () => clearInterval(t);
  }, [dispatch]);

  const totals = useMemo(() => {
    const video = safeCount(video_requests);
    const redo = safeCount(redo_exams);
    const homework = safeCount(hw);
    return {
      video,
      redo,
      homework,
      all: video + redo + homework,
    };
  }, [video_requests, redo_exams, hw]);

  const items = useMemo(
    () => [
      {
        key: "redo",
        label: "Exam redo requests",
        count: totals.redo,
        to: "/dashboard/exam-redo-requests",
      },
      {
        key: "video",
        label: "Video view requests",
        count: totals.video,
        to: "/dashboard/videos-more-view-requests",
      },
      {
        key: "hw",
        label: "Homework reports",
        count: totals.homework,
        to: "/dashboard/uncomment-homework-report",
      },
    ],
    [totals]
  );

  return (
    <Popover placement="top-end" closeOnBlur>
      <PopoverTrigger>
        <Box position="relative" flexShrink={0}>
          <IconButton
            aria-label="Notifications"
            icon={<FaBell size={18} />}
            variant={variant}
            color={iconColor}
            size={size}
            borderRadius="12px"
            isLoading={isLoading}
            _hover={{ bg: hoverBg }}
            _active={{ bg: hoverBg }}
          />
          {totals.all > 0 ? (
            <Badge
              position="absolute"
              top="-6px"
              right="-6px"
              bg="#D64545"
              color="white"
              borderRadius="full"
              fontSize="10px"
              px={2}
              py="2px"
              minW="20px"
              textAlign="center"
              boxShadow="0 6px 18px rgba(0,0,0,0.25)"
            >
              {totals.all}
            </Badge>
          ) : null}
        </Box>
      </PopoverTrigger>
      <PopoverContent
        w="280px"
        border="1px solid"
        borderColor="var(--border-subtle)"
        bg="var(--surface-raised)"
        boxShadow="lg"
        _focus={{ outline: "none" }}
      >
        <PopoverArrow bg="var(--surface-raised)" borderColor="var(--border-subtle)" />
        <PopoverHeader borderBottomWidth="1px" borderColor="#E6DACD" py={3}>
          <Flex align="center" justify="space-between">
            <Text fontWeight="800" color={brandPalette.primary}>
              Notifications
            </Text>
            {totals.all ? (
              <Text fontSize="xs" fontWeight="800" color="rgba(91, 13, 22, 0.65)">
                {totals.all} total
              </Text>
            ) : null}
          </Flex>
        </PopoverHeader>
        <PopoverBody p={3}>
          <VStack align="stretch" spacing={2}>
            {items.map((it) => (
              <Button
                key={it.key}
                w="100%"
                justifyContent="space-between"
                variant="ghost"
                bg="rgba(255,255,255,0.7)"
                border="1px solid"
                borderColor="rgba(214, 206, 192, 0.85)"
                borderRadius="lg"
                py={3}
                _hover={{ bg: "white" }}
                onClick={() => navigate(it.to)}
              >
                <Text fontWeight="800" color={brandPalette.primary} fontSize="sm">
                  {it.label}
                </Text>
                <Badge bg={brandPalette.secondary} color="white" borderRadius="full" px={2}>
                  {it.count}
                </Badge>
              </Button>
            ))}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

